import { useState, useEffect, useCallback } from 'react';
import P60FromAI2Empower from './P60FromAI2Empower';
import P61ServiceTeacher from './P61ServiceTeacher';
import P62ServiceStudent from './P62ServiceStudent';
import P63ServiceExpert from './P63ServiceExpert';
import P64ServiceManager from './P64ServiceManager';
import P65OutputList from './P65OutputList';
import P66GradingUsage from './P66GradingUsage';
import P67Compliance from './P67Compliance';
import P68WrapUp from './P68WrapUp';
import P69PointLineSurface from './P69PointLineSurface';
import P70TeacherProfile from './P70TeacherProfile';
import P71TeachingGroup from './P71TeachingGroup';
import P72SchoolMap from './P72SchoolMap';
import P73ProvinceMap from './P73ProvinceMap';
import P74CourseCoverage from './P74CourseCoverage';
import P75ModelComparison from './P75ModelComparison';
import P76MgmtQuestions from './P76MgmtQuestions';
import P77DataPipeline from './P77DataPipeline';
import P78PilotPath from './P78PilotPath';
import P79WrapUp from './P79WrapUp';

const TOTAL_SLIDES = 20;

export default function Section5Carousel() {
  const [activeSlide, setActiveSlide] = useState(0);
  const [direction, setDirection] = useState<'next' | 'prev' | 'initial'>('initial');

  const goNext = useCallback(() => {
    setDirection('next');
    setActiveSlide((prev) => (prev < TOTAL_SLIDES - 1 ? prev + 1 : prev));
  }, []);

  const goPrev = useCallback(() => {
    setDirection('prev');
    setActiveSlide((prev) => (prev > 0 ? prev - 1 : prev));
  }, []);

  const goTo = useCallback((i: number) => {
    setDirection(i > activeSlide ? 'next' : i < activeSlide ? 'prev' : 'initial');
    setActiveSlide(i);
  }, [activeSlide]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        goNext();
      }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        goPrev();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [goNext, goPrev]);

  const slides = [
    <P60FromAI2Empower forceVisible key="p60" />,
    <P61ServiceTeacher forceVisible key="p61" />,
    <P62ServiceStudent forceVisible key="p62" />,
    <P63ServiceExpert forceVisible key="p63" />,
    <P64ServiceManager forceVisible key="p64" />,
    <P65OutputList forceVisible key="p65" />,
    <P66GradingUsage forceVisible key="p66" />,
    <P67Compliance forceVisible key="p67" />,
    <P68WrapUp forceVisible key="p68" />,
    <P69PointLineSurface forceVisible key="p69" />,
    <P70TeacherProfile forceVisible key="p70" />,
    <P71TeachingGroup forceVisible key="p71" />,
    <P72SchoolMap forceVisible key="p72" />,
    <P73ProvinceMap forceVisible key="p73" />,
    <P74CourseCoverage forceVisible key="p74" />,
    <P75ModelComparison forceVisible key="p75" />,
    <P76MgmtQuestions forceVisible key="p76" />,
    <P77DataPipeline forceVisible key="p77" />,
    <P78PilotPath forceVisible key="p78" />,
    <P79WrapUp forceVisible key="p79" />,
  ];

  return (
    <section
      id="section-5"
      className="relative h-screen overflow-hidden bg-background-50"
    >
      {/* 浮动光斑 */}
      <div className="absolute top-1/3 left-[10%] w-52 h-52 rounded-full bg-accent-100/5 blur-[75px] pointer-events-none animate-orb-float-2" />
      <div className="absolute bottom-1/4 right-[8%] w-56 h-56 rounded-full bg-primary-100/5 blur-[85px] pointer-events-none animate-orb-float-3" />

      {slides.map((slide, i) => (
        <div
          key={i}
          className={`absolute inset-0 transition-all duration-600 ${
            activeSlide === i
              ? direction === 'next'
                ? 'animate-slide-in-right z-10 pointer-events-auto'
                : direction === 'prev'
                  ? 'animate-slide-in-left z-10 pointer-events-auto'
                  : 'animate-slide-initial z-10 pointer-events-auto'
              : 'opacity-0 z-0 pointer-events-none scale-[0.98]'
          }`}
        >
          {slide}
        </div>
      ))}

      <button
        onClick={goPrev}
        disabled={activeSlide === 0}
        className={`absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-30 w-11 h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer ${
          activeSlide === 0
            ? 'glass border border-background-200/40 text-foreground-300 cursor-default'
            : 'glass border border-gold-200/30 text-primary-600 hover:border-gold-300/50 hover:scale-110 active:scale-95'
        }`}
      >
        <i className="ri-arrow-left-s-line text-xl md:text-2xl" />
      </button>

      <button
        onClick={goNext}
        disabled={activeSlide === TOTAL_SLIDES - 1}
        className={`absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-30 w-11 h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer ${
          activeSlide === TOTAL_SLIDES - 1
            ? 'glass border border-background-200/40 text-foreground-300 cursor-default'
            : 'glass border border-gold-200/30 text-primary-600 hover:border-gold-300/50 hover:scale-110 active:scale-95'
        }`}
      >
        <i className="ri-arrow-right-s-line text-xl md:text-2xl" />
      </button>

      <div className="absolute bottom-6 md:bottom-8 left-1/2 -translate-x-1/2 z-30 flex items-center gap-1 flex-wrap justify-center px-4 max-w-[90vw]">
        {Array.from({ length: TOTAL_SLIDES }).map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`rounded-full transition-all duration-300 cursor-pointer ${
              activeSlide === i
                ? 'w-5 h-1.5 bg-gold-500'
                : 'w-1.5 h-1.5 bg-background-300/50 hover:bg-gold-400/60 hover:scale-125'
            }`}
          />
        ))}
      </div>

      <div className="absolute bottom-6 md:bottom-8 right-6 md:right-10 z-30">
        <span className="font-mono text-xs text-foreground-400 tracking-wider">
          {activeSlide + 1} / {TOTAL_SLIDES}
        </span>
      </div>
    </section>
  );
}